import { useState, useEffect, useMemo, useCallback } from 'react';
import { DateRange } from 'react-day-picker';
import { knowledgeService } from '@/services/kbService';

import { logger } from '@/utils/logger';

// Render a file extension as a friendly label ("Markdown" not "MD"). Used when
// deriving the File Type options from a document type's declared extensions.
const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF', doc: 'DOC', docx: 'DOCX', txt: 'TXT', md: 'Markdown',
  rtf: 'RTF', xls: 'XLS', xlsx: 'XLSX', csv: 'CSV', ppt: 'PPT', pptx: 'PPTX',
  png: 'PNG', jpg: 'JPG', jpeg: 'JPEG', gif: 'GIF', tiff: 'TIFF', bmp: 'BMP',
};
const labelForExt = (ext: string) => FILE_TYPE_LABELS[ext] ?? ext.toUpperCase();

export interface KBFilterState {
  searchQuery: string;
  fileTypeFilter: string;
  documentTypeFilter: string;
  minFileSize: string;
  maxFileSize: string;
  kbCategorized: string; // 'all' | 'true' | 'false'
  sourceFilter: string; // 'all' | 'manual' | 'google' | 'sharepoint'
  dateRange: DateRange | undefined;
  showFilters: boolean;
}

export interface KBFilterActions {
  setSearchQuery: (value: string) => void;
  setFileTypeFilter: (value: string) => void;
  setDocumentTypeFilter: (value: string) => void;
  setMinFileSize: (value: string) => void;
  setMaxFileSize: (value: string) => void;
  setKbCategorized: (value: string) => void;
  setSourceFilter: (value: string) => void;
  setDateRange: (value: DateRange | undefined) => void;
  setShowFilters: (value: boolean) => void;
  handleResetFilters: () => void;
  handleDocumentTypeChange: (val: string) => void;
  handleFileTypeChange: (val: string) => void;
}

export interface KBFilterComputed {
  hasActiveFilters: boolean;
  activeFilterCount: number;
  filteredFileTypeOptions: { value: string; label: string }[] | undefined;
  filteredDocTypeOptions: { value: string; label: string }[];
  documentTypes: Array<{ name: string; type_name: string; description: string; file_extensions: string }>;
  documentTypesLoading: boolean;
}

export type UseKBFiltersReturn = KBFilterState & KBFilterActions & KBFilterComputed;

export function useKBFilters(): UseKBFiltersReturn {
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('all');
  const [minFileSize, setMinFileSize] = useState<string>('');
  const [maxFileSize, setMaxFileSize] = useState<string>('');
  const [kbCategorized, setKbCategorized] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  // Document type data
  const [documentTypes, setDocumentTypes] = useState<Array<{ name: string; type_name: string; description: string; file_extensions: string }>>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);

  // Load document types on mount
  useEffect(() => {
    const loadDocumentTypes = async () => {
      setDocumentTypesLoading(true);
      try {
        const types = await knowledgeService.getActiveDocumentTypes();
        setDocumentTypes(types);
      } catch (error) {
        logger.error('Failed to load document types:', error);
      } finally {
        setDocumentTypesLoading(false);
      }
    };
    loadDocumentTypes();
  }, []);

  // Computed filter state
  const hasActiveFilters = fileTypeFilter !== 'all' || documentTypeFilter !== 'all' || kbCategorized !== 'all' || sourceFilter !== 'all' || !!minFileSize || !!maxFileSize || !!dateRange;
  const activeFilterCount = [
    fileTypeFilter !== 'all' ? fileTypeFilter : null,
    documentTypeFilter !== 'all' ? documentTypeFilter : null,
    kbCategorized !== 'all' ? kbCategorized : null,
    sourceFilter !== 'all' ? sourceFilter : null,
    minFileSize,
    maxFileSize,
    dateRange,
  ].filter(Boolean).length;

  
  const filteredFileTypeOptions = useMemo(() => {
    if (documentTypeFilter === 'all') return undefined;
    const dt = documentTypes.find(d => d.name === documentTypeFilter);
    if (!dt?.file_extensions) return undefined;
    const exts = dt.file_extensions
      .split(',')
      .map(e => e.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);
    if (!exts.length) return undefined;
    return [
      { value: 'all', label: 'All Types' },
      ...exts.map(ext => ({ value: ext, label: labelForExt(ext) })),
    ];
  }, [documentTypeFilter, documentTypes]);

  const filteredDocTypeOptions = useMemo(
    () => documentTypes.map(dt => ({ value: dt.name, label: dt.type_name || dt.name })),
    [documentTypes],
  );

  const handleDocumentTypeChange = useCallback((val: string) => {
    setDocumentTypeFilter(val);
    if (val !== 'all' && fileTypeFilter !== 'all') {
      const dt = documentTypes.find(d => d.name === val);
      if (dt?.file_extensions) {
        const exts = dt.file_extensions
          .split(',')
          .map(e => e.trim().toLowerCase().replace(/^\./, ''));
        if (!exts.includes(fileTypeFilter.toLowerCase())) {
          setFileTypeFilter('all');
        }
      }
    }
  }, [fileTypeFilter, documentTypes]);

  const handleFileTypeChange = useCallback((val: string) => {
    // No reverse coupling: changing the File Type must never touch Document Type.
    setFileTypeFilter(val);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setFileTypeFilter('all');
    setDocumentTypeFilter('all');
    setMinFileSize('');
    setMaxFileSize('');
    setKbCategorized('all');
    setSourceFilter('all');
    setDateRange(undefined);
  }, []);

  return {
    // State
    searchQuery,
    fileTypeFilter,
    documentTypeFilter,
    minFileSize,
    maxFileSize,
    kbCategorized,
    sourceFilter,
    dateRange,
    showFilters,
    // Actions
    setSearchQuery,
    setFileTypeFilter,
    setDocumentTypeFilter,
    setMinFileSize,
    setMaxFileSize,
    setKbCategorized,
    setSourceFilter,
    setDateRange,
    setShowFilters,
    handleResetFilters,
    handleDocumentTypeChange,
    handleFileTypeChange,
    // Computed
    hasActiveFilters,
    activeFilterCount,
    filteredFileTypeOptions,
    filteredDocTypeOptions,
    documentTypes,
    documentTypesLoading,
  };
}
